import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Alert, Button, Input, Modal, Radio, Segmented, Select, Space, Typography } from 'antd'
import { AppstoreOutlined, ExperimentOutlined, SearchOutlined } from '@ant-design/icons'
import { Titles } from '../../layouts/Titles'
import { CardList, ListCard, ListChip, ListMedia, ListToolbar, ListToolbarChips, ListToolbarField } from '../../components/card_list/CardList'
import i from '../../translations/i'
import superController, { SuperProject } from './super.controller'
import controller, { RolloutFilter, RolloutMode, userLabel } from './features.controller'
import { SuperGuard } from './SuperGuard'

// Включение функций платформы по проектам и пользователям: пока функция не включена, у проекта работает прежний код.
// Список функций и сама логика — на сервере (App\Domain\Features\Feature), здесь решаем, кому что включить

const MODES: RolloutMode[] = ['off', 'users', 'all']
const FILTERS: RolloutFilter[] = ['any', 'enabled', 'off']

const ModeChip = ({ project }: { project: SuperProject }) => {
    const rollout = controller.rolloutOf(project.id)

    if (rollout.mode === 'all') return <ListChip tone="success">{i.t('superAdmin:features.mode.allShort')}</ListChip>
    if (rollout.mode === 'users') {
        return <ListChip tone="info">{i.t('superAdmin:features.mode.usersShort', { count: rollout.user_ids.length })}</ListChip>
    }

    return <ListChip>{i.t('superAdmin:features.mode.offShort')}</ListChip>
}

const UsersPicker = observer(() => {
    // В выпадающем списке только найденные поиском; выбранные видны подписями в самом поле
    const label = (id: number) => userLabel(controller.users[id], id)

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Select
                mode="multiple"
                labelInValue
                showSearch
                filterOption={false}
                allowClear
                popupMatchSelectWidth={false}
                style={{ width: '100%' }}
                placeholder={i.t('superAdmin:features.usersPlaceholder')}
                value={controller.draftUserIds.map(id => ({ value: id, label: label(id) }))}
                loading={controller.usersSearching}
                notFoundContent={i.t('superAdmin:features.usersEmpty')}
                onSearch={text => controller.searchUsers(text)}
                onChange={(value: { value: number }[]) => controller.setUserIds(value.map(item => item.value))}
                options={controller.usersFound.map(id => ({ value: id, label: label(id) }))}
            />
            <Typography.Text type="secondary">{i.t('superAdmin:features.usersHint')}</Typography.Text>
            {controller.draftMode === 'users' && controller.draftUserIds.length === 0 &&
                <Typography.Text type="danger">{i.t('superAdmin:features.usersRequired')}</Typography.Text>
            }
        </div>
    )
})

const EditModal = observer(() => {
    const project = controller.editing
    const feature = controller.selected

    return (
        <Modal
            open={!!project}
            title={project ? `${project.name || project.bandl} · ID ${project.id}` : ''}
            onCancel={() => controller.close()}
            onOk={() => controller.save()}
            okText={i.t('def:actions.save')}
            cancelText={i.t('superAdmin:features.cancel')}
            okButtonProps={{ disabled: !controller.canSave, loading: controller.saving }}
            maskClosable={false}
            destroyOnClose
            centered
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Typography.Text strong>{feature?.title}</Typography.Text>

                <Radio.Group value={controller.draftMode} onChange={e => controller.setMode(e.target.value)}>
                    <Space direction="vertical" size={10}>
                        {MODES.map(mode => (
                            <Radio key={mode} value={mode}>
                                <b>{i.t(`superAdmin:features.mode.${mode}`)}</b>
                                <div><Typography.Text type="secondary">{i.t(`superAdmin:features.mode.${mode}Hint`)}</Typography.Text></div>
                            </Radio>
                        ))}
                    </Space>
                </Radio.Group>

                {controller.draftMode === 'users' && <UsersPicker />}
            </div>
        </Modal>
    )
})

const FeaturesPage = observer(() => {
    useEffect(() => {
        controller.load()
    }, [])

    const feature = controller.selected

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {controller.features.length > 1 &&
                <Segmented
                    block
                    value={controller.selectedKey ?? undefined}
                    options={controller.features.map(item => ({ value: item.key, label: item.title }))}
                    onChange={value => controller.select(`${value}`)}
                />
            }

            {feature &&
                <Alert
                    type="info"
                    showIcon
                    icon={<ExperimentOutlined />}
                    message={feature.title}
                    description={<>
                        <div>{feature.description}</div>
                        <div style={{ marginTop: 6 }}>{i.t('superAdmin:features.defaultHint')}</div>
                    </>}
                />
            }

            <CardList
                items={controller.filteredProjects}
                loading={controller.loading || superController.projectsLoading}
                count={controller.filteredProjects.length}
                toolbar={
                    <ListToolbar>
                        <ListToolbarField wide>
                            <Input
                                allowClear
                                prefix={<SearchOutlined />}
                                placeholder={i.t('superAdmin:projects.search')}
                                value={superController.projectsSearch}
                                onChange={e => superController.projectsSearch = e.target.value}
                            />
                        </ListToolbarField>
                        <ListToolbarChips>
                            {FILTERS.map(filter => (
                                <ListChip key={filter} active={controller.filter === filter} onClick={() => controller.filter = filter}>
                                    {`${i.t(`superAdmin:features.filters.${filter}`)} · ${controller.counts[filter]}`}
                                </ListChip>
                            ))}
                        </ListToolbarChips>
                    </ListToolbar>
                }
                empty={{ icon: <AppstoreOutlined />, title: i.t('superAdmin:projects.empty') }}
                renderItem={(project: SuperProject) => (
                    <ListCard
                        media={<ListMedia icon={`${project.name ?? ''}`.trim().charAt(0).toUpperCase() || '?'} tone={controller.rolloutOf(project.id).mode === 'off' ? 'gray' : 'green'} />}
                        title={project.name || project.bandl}
                        subtitle={`ID ${project.id}`}
                        onOpen={() => controller.edit(project)}
                        chips={<>
                            <ListChip copy={project.bandl}>{project.bandl}</ListChip>
                            <ModeChip project={project} />
                        </>}
                        actions={<Button onClick={() => controller.edit(project)}>{i.t('superAdmin:features.configure')}</Button>}
                    />
                )}
            />

            <EditModal />
        </div>
    )
})

export const SuperFeatures = () => (
    <>
        <Titles
            title={i.t('superAdmin:hub.features')}
            breadcrumb={[
                { title: i.t('def:settings'), href: '/settings' },
                { title: i.t('superAdmin:title') },
                { title: i.t('superAdmin:hub.features') },
            ]}
        />
        <SuperGuard>
            <FeaturesPage />
        </SuperGuard>
    </>
)
